/**
 * The signed-in shell: a skip link, the header with the mark, the navigation (data, nav/nav-config.ts), who is signed
 * in, the language and sign-out; a demonstration banner when the installation says it is one; then the page.
 */

import type { ReactNode } from 'react'
import { Link, NavLink } from 'react-router-dom'

import { LANGUAGES } from '../i18n/core'
import { useLanguage } from '../i18n/LanguageProvider'
import { useSession } from '../lib/session'
import { navFor } from '../nav/nav-config'
import { Mark } from './Mark'

export function Shell({ demo, children }: { demo: boolean; children: ReactNode }) {
  const { t, language, setLanguage } = useLanguage()
  const { person, signOut } = useSession()
  return (
    <div className="flex min-h-full flex-col">
      <a href="#main" className="skip-link">
        {t('nav.skip')}
      </a>
      {demo && (
        <p className="bg-accent-soft px-4 py-2 text-center text-ink" role="note">
          {t('demo.banner')}
        </p>
      )}
      <header className="border-b border-line bg-card">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3 sm:px-6">
          <Link to="/board" className="flex items-center gap-2 font-semibold text-ink">
            <Mark size={28} label={t('app.name')} />
            <span>{t('app.name')}</span>
          </Link>
          <nav aria-label={t('nav.main')} className="flex flex-wrap gap-1">
            {navFor(person.role).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => `rounded-md px-3 py-1.5 ${isActive ? 'bg-accent-soft font-medium text-ink' : 'text-muted hover:text-ink'}`}
              >
                {t(item.key)}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex flex-wrap items-center gap-3 text-sm">
            <span className="text-muted">
              {t('shell.signedInAs', { name: person.displayName })}
              {person.role && <> · {t(`role.${person.role}`)}</>}
            </span>
            <span className="flex gap-1" role="group" aria-label={t('nav.language')}>
              {LANGUAGES.map((l) => (
                <button key={l.value} type="button" lang={l.value} aria-pressed={language === l.value} onClick={() => setLanguage(l.value)} className="rounded px-2 uppercase text-ink hover:bg-surface">
                  {l.value}
                </button>
              ))}
            </span>
            <button type="button" onClick={() => void signOut()} className="rounded-md border border-line px-3 py-1 text-ink hover:bg-surface">
              {t('nav.signOut')}
            </button>
          </div>
        </div>
      </header>
      <main id="main" className="flex-1">
        {children}
      </main>
      <footer className="border-t border-line px-4 py-3 text-center text-sm text-muted">
        <Link to="/about" className="underline">
          {t('nav.about')}
        </Link>
        {' · '}
        <Link to="/privacy/at-work" className="underline">
          {t('nav.atWork')}
        </Link>
        <Link to="/privacy" className="underline">
          {t('nav.privacy')}
        </Link>
      </footer>
    </div>
  )
}
